import { useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { TokenRegistrationService } from '../services/token-registration';
import { datadogRum } from '@datadog/browser-rum';

// Simple JWT decoder that only decodes the payload (no signature verification)
const decodeJwtPayload = (token: string) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    const payload = parts[1];

    if (!payload) {
      return null;
    }

    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);

    const decoded = atob(padded);
    return JSON.parse(decoded);
  } catch (error) {
    console.warn('Failed to decode JWT payload:', error);
    return null;
  }
};

export const useTokenRegistration = () => {
  const { getAccessTokenSilently, logout } = useAuth0();

  const registerToken = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();

      // Decode the JWT to get sub and iat claims
      const decodedToken = decodeJwtPayload(token);
      const userSub = decodedToken?.sub;
      const tokenIat = decodedToken?.iat;

      if (userSub && tokenIat) {
        // Create a unique key for this specific token
        const tokenRegistrationKey = `token_registered_${userSub}_${tokenIat}`;

        // Check if this specific token has already been registered
        const hasRegisteredThisToken =
          localStorage.getItem(tokenRegistrationKey);

        if (!hasRegisteredThisToken) {
          const service = new TokenRegistrationService();
          await service.registerToken(token);

          // Mark this token as registered
          localStorage.setItem(tokenRegistrationKey, 'true');

          // Log success to Datadog
          datadogRum.addAction('token_registration_success', {
            userSub,
            tokenIat,
            action: 'token_registered',
          });
        } else {
          // Token already registered, log to Datadog
          datadogRum.addAction('token_registration_skipped', {
            userSub,
            tokenIat,
            action: 'token_already_registered',
          });
        }
      } else {
        // Fallback: register token if we can't get sub or iat
        const service = new TokenRegistrationService();
        await service.registerToken(token);

        // Log fallback registration to Datadog
        datadogRum.addAction('token_registration_fallback', {
          action: 'token_registered_without_claims',
        });
      }
    } catch (error) {
      console.error('Failed to register token:', error);
      datadogRum.addError(error, {
        action: 'token_registration_failed',
      });
    }
  }, [getAccessTokenSilently]);

  const invalidateTokenAndLogout = useCallback(async () => {
    const token = await getAccessTokenSilently();

    const service = new TokenRegistrationService();

    await service.invalidateToken({ token }).finally(() => {
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.includes('@@auth0spajs@@') ||
            key.startsWith('token_registered_'))
        ) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));

      logout({
        returnTo: `${window.location.origin}/login`,
      });
    });
  }, [logout, getAccessTokenSilently]);

  return { registerToken, invalidateTokenAndLogout };
};
